"use client";

import Image from "@/components/ui/OptimizedImage";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type UrgencyIconName =
  | "TRENDING_UP"
  | "ZAP"
  | "STAR"
  | "FLAME"
  | "ALERT_TRIANGLE"
  | "ALERT_CIRCLE";

type UrgencyTierConfig = {
  id: string;
  minPercent: number;
  maxPercent: number;
  label: string;
  icon: UrgencyIconName;
  accentColor: string;
  badgeBg: string;
  badgeBorder: string;
  barColor: string;
  pulse: boolean;
};

type SuperGuestSummary = {
  isSuperGuest: boolean;
  name?: string;
  maxInvites?: number;
  invitesSent?: number;
  invitesLeft?: number;
};

export interface EventSidebarProps {
  /* Event info card */
  dateLabel: string;
  timeLabel: string;
  isOnlineEvent: boolean;
  venue: string;
  location: string;
  priceLabel: string;
  startingFromPriceLabel: string;
  hasTicketTiers: boolean;
  ticketTiersCount: number;

  /* Access card */
  rsvpState: string;
  isEnded: boolean;
  isCancelled: boolean;
  needsRequest: boolean;
  requiresHostApproval?: boolean;
  isPromoted: boolean;
  urgencyTier: UrgencyTierConfig | null;
  capacityTotal: number | null;
  spotsLeft: number | null;
  capacityLeftLabel: string;
  capacityTotalLabel: string;
  percentSold: number | null;

  /* Host card */
  hostLabel: string;
  hostImageUrl: string | null;
  ratingLabel: string;
  hostStats: { hostedCount: number; ratingAverage: number; ratingCount: number } | null;

  /* Accepted payments */
  showPaymentMethods: boolean;
  acceptedPaymentMethods: string[];

  /* Location card */
  mapImageUrl: string;
  mapUrl: string;
  locationPlaceName?: string | null;
  locationDetails?: string | null;
  showAddress?: boolean;
  onlineUrl?: string | null;

  /* Super guest invite */
  superGuest: SuperGuestSummary | null;
  invitesLeft: number;
  inviteInput: string;
  setInviteInput: (value: string) => void;
  inviteStatus: "idle" | "sending" | "sent";
  inviteError: string | null;
  canInvite: boolean;
  handleSendInvites: () => void;

  /* Report */
  onReportOpen: () => void;
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function CalendarIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 11h18" />
    </svg>
  );
}

function PinIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function CardIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h4" />
    </svg>
  );
}

function LockIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 118 0v3" />
    </svg>
  );
}

function GlobeIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
      <path d="M2 12h20" />
    </svg>
  );
}

function UrgencyIcon({ icon, className = "h-4 w-4" }: { icon: UrgencyIconName; className?: string }) {
  if (icon === "TRENDING_UP") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 17l6-6 4 4 7-7" />
        <path d="M14 8h6v6" />
      </svg>
    );
  }
  if (icon === "ZAP") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
      </svg>
    );
  }
  if (icon === "STAR") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M12 2l2.9 6.2 6.8 1-4.9 4.8 1.2 6.8L12 17.7 6 20.8l1.2-6.8L2.3 9.2l6.8-1L12 2z" />
      </svg>
    );
  }
  if (icon === "FLAME") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 3s4 3.5 4 7.5A4 4 0 0112 14a4 4 0 01-4-3.5C8 7 12 3 12 3z" />
        <path d="M12 14c3.3 0 6 2.5 6 5.5S15.3 22 12 22s-6-2-6-2.5S8.7 14 12 14z" />
      </svg>
    );
  }
  if (icon === "ALERT_TRIANGLE") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 3l10 18H2L12 3z" />
        <path d="M12 9v5m0 4h.01" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5m0 4h.01" />
    </svg>
  );
}

function AvatarCircle({ label }: { label: string }) {
  const initials = label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return (
    <div className="flex h-full w-full items-center justify-center rounded-full text-xs font-semibold text-[var(--hopp-text)] bg-[rgba(28,28,32,0.6)]">
      {initials || "H"}
    </div>
  );
}

function PaymentIcon({ type }: { type: "cash" | "upi" | "card" | "bank" | "wallet" }) {
  switch (type) {
    case "cash":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="3" />
          <path d="M2 10h2M20 10h2M2 14h2M20 14h2" />
        </svg>
      );
    case "upi":
      return <span className="text-[11px] font-bold tracking-[0.2em]">UPI</span>;
    case "card":
      return (
        <svg viewBox="0 0 36 16" className="h-4 w-9" aria-hidden="true">
          <circle cx="12" cy="8" r="6" fill="#EB001B" />
          <circle cx="20" cy="8" r="6" fill="#F79E1B" fillOpacity="0.9" />
        </svg>
      );
    case "bank":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M3 10h18" />
          <path d="M5 10V7l7-3 7 3v3" />
          <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
          <path d="M3 18h18" />
        </svg>
      );
    case "wallet":
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M16 12h2" />
          <path d="M2 10h20" />
        </svg>
      );
    default:
      return null;
  }
}

const PAYMENT_METHOD_CONFIG: Record<string, { label: string; icon: "cash" | "upi" | "card" | "bank" | "wallet" }> = {
  CASH: { label: "Cash", icon: "cash" },
  UPI: { label: "UPI", icon: "upi" },
  CARD: { label: "Cards", icon: "card" },
  NET_BANKING: { label: "Net Banking", icon: "bank" },
  WALLET: { label: "Wallet", icon: "wallet" },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function EventSidebar({
  dateLabel,
  timeLabel,
  isOnlineEvent,
  venue,
  location,
  priceLabel,
  startingFromPriceLabel,
  hasTicketTiers,
  ticketTiersCount,
  rsvpState,
  isEnded,
  isCancelled,
  needsRequest,
  requiresHostApproval,
  isPromoted,
  urgencyTier,
  capacityTotal,
  spotsLeft,
  capacityLeftLabel,
  capacityTotalLabel,
  percentSold,
  hostLabel,
  hostImageUrl,
  ratingLabel,
  hostStats,
  showPaymentMethods,
  acceptedPaymentMethods,
  mapImageUrl,
  mapUrl,
  locationPlaceName,
  locationDetails,
  showAddress,
  onlineUrl,
  superGuest,
  invitesLeft,
  inviteInput,
  setInviteInput,
  inviteStatus,
  inviteError,
  canInvite,
  handleSendInvites,
  onReportOpen,
}: EventSidebarProps) {


  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-20 lg:self-start">

      {/* Event Info Card */}
      <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
        <div className="flex items-center justify-between mb-5">
          <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Event Info</span>
        </div>
        {/* Date row */}
        <div className="flex items-center gap-3.5 py-2">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-white/[0.04] text-white/60">
            <CalendarIcon className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="font-medium text-[13px] text-white">{dateLabel}</p>
            <p className="text-xs text-white/50">{timeLabel}</p>
          </div>
        </div>
        {/* Venue row */}
        <div className="flex items-center gap-3.5 py-2 border-t border-white/[0.08]">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-white/[0.04] text-white/60">
            <PinIcon className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="font-medium text-[13px] text-white">{isOnlineEvent ? "Online" : venue}</p>
            <p className="text-xs text-white/50">{isOnlineEvent ? "Virtual event" : location}</p>
          </div>
        </div>
        {/* Price row */}
        <div className="flex items-center gap-3.5 py-2 border-t border-white/[0.08]">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-white/[0.04] text-white/60">
            <CardIcon className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="font-medium text-[13px] text-white">{hasTicketTiers ? <>{`Starting from ${startingFromPriceLabel}`} {startingFromPriceLabel !== "Free" && <span className="text-[11px] font-normal text-white/50">+ platform fee</span>}</> : priceLabel}</p>
            {hasTicketTiers ? <p className="text-xs text-white/50">{ticketTiersCount} tier{ticketTiersCount !== 1 ? "s" : ""} available</p> : null}
          </div>
        </div>
      </div>

      {/* Access Card */}
      <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
        <div className="flex items-center justify-between mb-5">
          <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Access</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: urgencyTier?.accentColor ?? "var(--hopp-gold)",
              boxShadow: `0 0 8px ${
                urgencyTier?.accentColor
                  ? `${urgencyTier.accentColor}66`
                  : "rgba(212,175,55,0.35)"
              }`,
            }}
          />
          <span className="text-[13px] text-white">
            {isEnded ? "Event ended" : isCancelled ? "Event cancelled" : rsvpState === "approved" ? "Approved" : rsvpState === "confirmed" ? "Confirmed" : rsvpState === "pending" ? "Pending approval" : needsRequest ? "Request access" : "Open"}
          </span>
        </div>
        {isPromoted && urgencyTier ? (
          <>
            <div
              className={`mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${urgencyTier.pulse ? "animate-pulse" : ""}`}
              style={{
                color: urgencyTier.accentColor,
                borderColor: urgencyTier.badgeBorder,
                backgroundColor: urgencyTier.badgeBg,
              }}
            >
              <UrgencyIcon icon={urgencyTier.icon} className="h-3.5 w-3.5" />
              <span className="font-medium">{urgencyTier.label}</span>
            </div>
            {/* Capacity bar hidden — re-enable when ready */}
          </>
        ) : null}
        {requiresHostApproval && rsvpState === "approved" ? (
          <div className="mt-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 p-3">
            <div className="flex items-start gap-2.5">
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-[13px] font-medium text-emerald-400">You&apos;re approved!</p>
                <p className="text-xs text-white/50 mt-0.5">Complete your registration.</p>
              </div>
            </div>
          </div>
        ) : requiresHostApproval && rsvpState === "pending" ? (
          <div className="mt-3 rounded-lg bg-amber-500/5 border border-amber-500/10 p-3">
            <div className="flex items-start gap-2.5">
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-[13px] font-medium text-amber-400">Your request is being reviewed</p>
                <p className="text-xs text-white/50 mt-0.5">The host will review and respond to your request.</p>
              </div>
            </div>
          </div>
        ) : requiresHostApproval && rsvpState === "rejected" ? (
          <div className="mt-3 rounded-lg bg-red-500/5 border border-red-500/10 p-3">
            <div className="flex items-start gap-2.5">
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <p className="text-[13px] font-medium text-red-400">Your request was not approved</p>
                <p className="text-xs text-white/50 mt-0.5">You can request access again.</p>
              </div>
            </div>
          </div>
        ) : requiresHostApproval && needsRequest ? (
          <div className="mt-3 rounded-lg bg-amber-500/5 border border-amber-500/10 p-3">
            <div className="flex items-start gap-2.5">
              <LockIcon className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-medium text-amber-400">Host approval required</p>
                <p className="text-xs text-white/50 mt-0.5">Request access and the host will review your request.</p>
              </div>
            </div>
          </div>
        ) : null}

      </div>

      {/* Host Card */}
      <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Host</span>
        </div>
        <div className="flex items-center gap-3.5">
          <div className="relative w-[52px] h-[52px] rounded-full overflow-hidden flex-shrink-0 border-2 border-white/[0.08]">
            {hostImageUrl ? (
              <Image src={hostImageUrl} alt={hostLabel} width={52} height={52} className="w-[52px] h-[52px] rounded-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-full bg-[rgba(28,28,32,0.6)]">
                <Image src="/images/logo.svg" alt="Hopp" width={32} height={32} className="object-contain" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-[15px] text-white truncate">{hostLabel}</p>
          </div>
        </div>
      </div>

      {/* Accepted Payments Card */}
      {showPaymentMethods ? (
        <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Accepted Payments</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {acceptedPaymentMethods.map((method) => {
              const config = PAYMENT_METHOD_CONFIG[method];
              if (!config) return null;
              return (
                <span key={method} className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[10px] font-semibold text-white/50 uppercase tracking-[0.06em] flex items-center gap-1.5">
                  <PaymentIcon type={config.icon} />
                  {config.label}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Location Card */}
      {!isOnlineEvent ? (
        <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Location</span>
          </div>
          {/* Map */}
          <div className="w-full aspect-[16/9] rounded-[14px] overflow-hidden bg-white/[0.04] mb-3.5">
            {mapImageUrl ? (
              <div className="relative w-full h-full">
                <Image src={mapImageUrl} alt={`Map of ${locationPlaceName ?? venue}`} fill className="object-cover" loading="lazy" sizes="(max-width: 1024px) 100vw, 380px" />
              </div>
            ) : (
              <div className="w-full h-full bg-white/[0.04] flex items-center justify-center text-white/20">
                <PinIcon className="h-8 w-8" />
              </div>
            )}
          </div>
          <p className="font-medium text-[13px] text-white">{venue}</p>
          <p className="text-xs text-white/50 mt-0.5">{location}</p>
          {locationDetails ? (
            <p className="mt-1 text-xs italic text-white/50">{locationDetails}</p>
          ) : null}
          {showAddress === false && !isEnded && rsvpState !== "confirmed" ? (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[rgba(58,92,255,0.08)] border border-[rgba(58,92,255,0.15)] text-xs text-[#4A8BFF] mt-2.5">
              <LockIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{requiresHostApproval ? "Address shared upon confirmation" : "Exact address shared after RSVP"}</span>
            </div>
          ) : null}
          <div className="flex gap-2 mt-3.5">
            <a
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 py-2.5 text-center rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/60 text-xs font-medium tracking-[0.03em] hover:text-white hover:border-white/[0.12] transition"
            >
              Get directions
            </a>
            <button
              type="button"
              onClick={() => { if (mapUrl) window.open(mapUrl, "_blank"); }}
              className="flex-1 py-2.5 text-center rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/60 text-xs font-medium tracking-[0.03em] hover:text-white hover:border-white/[0.12] transition"
            >
              Open map
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Location</span>
          </div>
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(58,92,255,0.12)]">
              <GlobeIcon className="h-5 w-5 text-[var(--hopp-sapphire)]" />
            </div>
            <p className="text-sm font-medium text-white">Online event</p>
            {rsvpState === "confirmed" ? (
              onlineUrl ? (
                <a href={onlineUrl} target="_blank" rel="noreferrer" className="text-xs text-[#4A8BFF] hover:underline">
                  Join event
                </a>
              ) : (
                <p className="text-xs text-white/50">Link will be shared closer to start time.</p>
              )
            ) : (
              <p className="text-xs text-white/50">Link shared after RSVP.</p>
            )}
          </div>
        </div>
      )}

      {/* Super Guest Invite Card */}
      {superGuest?.isSuperGuest ? (
        <div className="bg-[#0c0c18] border border-white/[0.08] rounded-[20px] p-7 transition-colors hover:border-white/[0.12]">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-white/50">Invite Guests</span>
            <span className="text-xs text-white/50">{invitesLeft} left</span>
          </div>
          <p className="text-xs text-white/50 mb-3">Add email addresses separated by commas or spaces.</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={inviteInput}
              onChange={(eventInput) => setInviteInput(eventInput.target.value)}
              placeholder="guest1@email.com, guest2@email.com"
              className="w-full rounded-lg px-4 py-2.5 text-xs text-white/80 focus:outline-none bg-white/[0.04] border border-white/[0.08] placeholder:text-white/25"
            />
            <button
              onClick={handleSendInvites}
              disabled={!canInvite || inviteStatus === "sending"}
              className="rounded-lg px-5 py-2.5 text-xs font-medium text-white/60 transition hover:text-white disabled:opacity-50 bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.12] tracking-[0.03em]"
            >
              {inviteStatus === "sending" ? "Sending..." : inviteStatus === "sent" ? "Sent" : "Send"}
            </button>
          </div>
          {!canInvite ? <p className="mt-2 text-xs text-white/50">No invites remaining.</p> : null}
          {inviteError ? <p className="mt-2 text-xs text-red-400">{inviteError}</p> : null}
        </div>
      ) : null}

      {/* Report button */}
      <button
        onClick={onReportOpen}
        className="w-full py-2.5 text-center rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/50 text-xs font-medium tracking-[0.03em] hover:text-red-400 hover:border-red-400/20 transition"
      >
        Report this event
      </button>
    </aside>
  );
}
